import { usePage } from '@inertiajs/react';

import { query, url } from '../Shared/helpers';
import { Charity, Locale } from '../Shared/types';
import { PageProps } from './types';

function path(...segments: (string | number)[]) {
  return ['', 'b', ...segments].join('/');
}

export function useRouter() {
  const { props: { charity, locale }, url: currentUrl } = usePage<PageProps<{charity?: Charity | null; }>>();
  const charityId = charity?.id || '';

  const routes = {
    'home': (newCharityId?: string) => path(newCharityId || charityId),

    // Auth: GET
    'login_page': () => `/login?locale=${locale}`,
    // Auth: POST
    'logout': () => '/logout',
    'register': () => path('register'),

    // Charity: GET
    'add_charity_page': () => path('charity', 'add'),
    // Charity: POST
    'add_charity': () => path('charity'),

    // Reports: GET
    'reports_page': () => path(charityId, 'reports'),
    'download_transactions_report': (params: { start_date?: string; end_date?: string; }) => query(path(charityId, 'reports', 'transactions'), params),

    // Users: GET
    'users_page': () => path(charityId, 'users'),
    // Users: POST
    'add_user': () => path(charityId, 'users'),
    'delete_user': (userId: string) => path(charityId, 'users', userId, 'delete'),
    'edit_user_role': (userId: string) => path(charityId, 'users', userId, 'edit_role'),

    // Charity: GET
    'charity_settings_page': () => path(charityId, 'charity'),
    'integrations_page': () => path(charityId, 'charity', 'integrations'),
    'invoices_page': () => path(charityId, 'charity', 'invoices'),
    'mail_settings_page': () => path(charityId, 'charity', 'mail'),
    'event_settings_page': () => path(charityId, 'charity', 'events'),

    // Charity: POST
    'save_settings': () => path(charityId, 'charity'),
    'save_integrations': () => path(charityId, 'charity/integrations'),
    'save_mail_settings': () => path(charityId, 'charity/mail'),
    'save_event_settings': () => path(charityId, 'charity/events'),

    // Onboarding: GET
    'onboarding_page': () => path(charityId, 'onboarding'),

    // Dashboard: GET
    'dashboard_page': () => path(charityId, 'dashboard'),
    'mail_dashboard_page': () => path(charityId, 'dashboard', 'mail'),

    // Projects: GET
    'project_page': (projectId: string) => path(charityId, 'settings', 'projects', projectId),
    'edit_project_page': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'edit'),
    'project_campaigns_json': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'campaigns.json'),
    'project_events_json': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'events.json'),
    'project_mails_json': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'mails.json'),
    'project_mails_page': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'mails'),
    'edit_project_mail_page': (projectId: string, name: string) => path(charityId, 'settings', 'projects', projectId, 'project_mails', name),
    'edit_custom_mail_page': (projectId: string, mailId: string) => path(charityId, 'settings', 'projects', projectId, 'custom_mails', mailId),
    'custom_mail_campaigns_json': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'custom_mail_campaigns.json'),
    'projects_page': () => path(charityId, 'settings', 'projects'),

    // Projects: POST
    'add_project': () => path(charityId, 'settings', 'projects'),
    'edit_project': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'edit'),
    'edit_project_mails': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'project_mails', 'edit'),
    'toggle_project_mail': (projectId: string, name: string) => path(charityId, 'settings', 'projects', projectId, 'project_mails', name, 'toggle'),
    'toggle_project': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'toggle'),
    'delete_project': (projectId: string) => path(charityId, 'settings', 'projects', projectId, 'delete'),

    // Products: GET
    'product_page': (productId: string) => path(charityId, 'settings', 'products', productId),
    'products_page': () => path(charityId, 'settings', 'products'),

    // Products: POST
    'edit_product': (productId: string) => path(charityId, 'settings', 'products', productId, 'edit'),
    'toggle_product': (productId: string) => path(charityId, 'settings', 'products', productId, 'toggle'),
    'delete_product': (productId: string) => path(charityId, 'settings', 'products', productId, 'delete'),
    'add_product': () => path(charityId, 'settings', 'products'),

    // Donation forms: GET
    'donation_form_page': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId),
    'donation_form_settings_page': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId, 'settings'),
    'donation_form_mails_page': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId, 'mails'),
    'donation_forms_page': () => path(charityId, 'settings', 'donation_forms'),

    // Donation forms: POST
    'add_donation_form': () => path(charityId, 'settings', 'donation_forms'),
    'edit_donation_form_settings': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId, 'edit_settings'),
    'edit_donation_form_mails': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId, 'edit_mails'),
    'toggle_donation_form': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId, 'toggle'),
    'delete_donation_form': (donationFormId: string) => path(charityId, 'settings', 'donation_forms', donationFormId, 'delete'),

    // Mails: POST
    'add_mail': () => path(charityId, 'mails'),
    'import_mails': () => path(charityId, 'mails', 'import'),
    'edit_mail': (mailId: string) => path(charityId, 'mails', mailId, 'edit'),
    'delete_mail': (mailId: string) => path(charityId, 'mails', mailId, 'delete'),
    'toggle_mail': (mailId: string) => path(charityId, 'mails', mailId, 'toggle'),

    // Events: GET
    'event_page': (eventId: string) => path(charityId, 'settings', 'events', eventId),
    'events_page': () => path(charityId, 'settings', 'events'),
    // Events: POST
    'create_project': (eventId: string) => path(charityId, 'settings', 'events', eventId, 'create_project'),

    // Campaigns: GET
    'campaign_page': (campaignId: string) => path(charityId, 'campaigns', campaignId),
    'campaign_sent_mails_page': (campaignId: string) => path(charityId, 'campaigns', campaignId, 'sent_mails'),
    'campaigns_page': () => path(charityId, 'campaigns'),
    // Campaigns: POST
    'add_campaign': () => path(charityId, 'campaigns'),
    'add_offline_donation': (campaignId: string) => path(charityId, 'campaigns', campaignId, 'add_offline_donation'),
    'edit_campaign': (campaignId: string) => path(charityId, 'campaigns', campaignId, 'edit'),
    'toggle_campaign': (campaignId: string) => path(charityId, 'campaigns', campaignId, 'toggle'),
    'delete_campaign': (campaignId: string) => path(charityId, 'campaigns', campaignId, 'delete'),

    /**
     * Donations
     */
    // Donation: GET
    'donations_page': () => path(charityId, 'donations', 'oneoff'),
    'donation_page': (donationId: string) => path(charityId, 'donations', 'oneoff', donationId),

    // Donation: POST
    'edit_donation': (donationId: string) => path(charityId, 'donations', donationId, 'edit'),
    'delete_donation': (donationId: string) => path(charityId, 'donations', donationId, 'delete'),

    // Subscriptions: GET
    'subscriptions_page': () => path(charityId, 'donations', 'recurring'),
    'subscription_page': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId),
    'subscription_payments_page': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId, 'payments'),
    'subscription_sent_mails_page': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId, 'sent_mails'),

    // Subscriptions: POST
    'edit_subscription': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId, 'edit'),
    'send_subcription_confirmation_mail': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId, 'send_confirmation_mail'),
    'send_subcription_cancellation_mail': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId, 'send_cancellation_mail'),
    'delete_subscription': (subscriptionId: string) => path(charityId, 'donations', 'recurring', subscriptionId, 'delete'),
    'add_subscription': () => path(charityId, 'donations', 'recurring'),

    // Mollie: GET
    'mollie_page': () => path(charityId, 'mollie'),
    'mollie_authorization': () => path('mollie', 'authorize', charityId),

    // Mollie: POST
    'edit_payment_reference': () => path(charityId, 'mollie', 'edit_payment_reference'),
    'delete_mollie_connection': (mollieConnectionId: string) => path(charityId, 'mollie', 'connections', mollieConnectionId, 'delete'),
    'set_default_mollie_profile': (mollieProfileId: string) => path(charityId, 'mollie', 'profiles', mollieProfileId, 'set_default'),

    /**
     * Embeds
     */
    'embeds_page': () => path(charityId, 'embeds'),
    'embed_campaign_overview_page': () => path(charityId, 'embeds', 'campaign_overview'),
    'embed_campaigns_count_page': () => path(charityId, 'embeds', 'campaigns_count'),
    'embed_funds_raised_page': () => path(charityId, 'embeds', 'funds_raised'),

    /**
     * Sent mails
     */
    'sent_mail': (sentMailId: string) => path(charityId, 'sent_mails', sentMailId),

    /**
     * Website
     */
    'website.home_page': (locale: Locale, charityId?: string | null, eventId?: string | null) => url([locale, (charityId || eventId) && 'fundraisers', charityId || (eventId && 'all'), eventId].filter((value) => value).join('/')),
    'website.create_campaign_page': (charityId: string | null, params: { eventId?: string | null } = {}) => url(`${charityId}/create`, { event_id: params.eventId }),
  };

  const isActive = (route: string) => {
    return currentUrl.startsWith(route);
  };

  return {
    routes,
    isActive,
  };
}
